import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';
import {
  Card, Button, Spinner, Badge, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell,
  SearchInput,
} from '../../components/UI';
import { Pagination } from '../../components/UI/Pagination';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';
import {
  HiOutlineInboxIn, HiOutlineMail, HiOutlinePhone, HiOutlineOfficeBuilding,
  HiOutlineX,
} from 'react-icons/hi';

const SOURCE_LABEL = {
  contact: 'Contact',
  book_demo: 'Book a Demo',
  quick_connect: 'Connect (QR)',
};

const SOURCE_BADGE = {
  contact: 'info',
  book_demo: 'primary',
  quick_connect: 'warning',
};

const STATUS_BADGE = {
  new: 'new',
  contacted: 'follow_up',
  converted: 'converted',
  closed: 'closed',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const EMPTY_FILTERS = { source: '', status: '', search: '', from: '', to: '' };
const STATUSES = ['new', 'contacted', 'converted', 'closed'];
const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : '');

const toApiParams = ({ from, to, ...rest }) => ({ ...rest, ...dateRangeParams(from, to) });

const EXPORT_COLUMNS = [
  { header: 'Name', value: (l) => l.name },
  { header: 'Email', value: (l) => l.email },
  { header: 'Phone', value: (l) => l.phone },
  { header: 'Company', value: (l) => l.company },
  { header: 'City', value: (l) => l.city },
  { header: 'Source', value: (l) => SOURCE_LABEL[l.source] || l.source },
  { header: 'Status', value: (l) => capitalize(l.status) },
  { header: 'Interested In', value: (l) => l.interest },
  { header: 'Team Size', value: (l) => l.teamSize },
  { header: 'Industry', value: (l) => l.industry },
  { header: 'Preferred Date', value: (l) => l.preferredDate },
  { header: 'Preferred Time', value: (l) => l.preferredTime },
  { header: 'Message', value: (l) => l.message },
  { header: 'Internal Notes', value: (l) => l.notes },
  { header: 'Submitted', value: (l) => formatExportDate(l.createdAt) },
];

const WebsiteLeadsList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ page: 1, limit: 20, ...EMPTY_FILTERS });
  const [viewing, setViewing] = useState(null);
  const filtersActive = Object.keys(EMPTY_FILTERS).some((k) => filters[k]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin-website-leads', filters],
    queryFn: () => superAdminAPI.getWebsiteLeads(toApiParams(filters)),
  });

  const leads = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};
  const newCount = data?.data?.newCount || 0;

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => superAdminAPI.updateWebsiteLead(id, body),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['superadmin-website-leads']);
      toast.success('Lead updated');
      setViewing((prev) => (prev ? { ...prev, ...res.data?.data } : prev));
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update lead'),
  });

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Submissions from the landing page's Contact, Book a Demo, and Connect (QR) forms.
          </p>
        </div>
        {newCount > 0 && <Badge variant="new" size="md">{newCount} new</Badge>}
      </div>

      <Card padding="p-4" className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <SearchInput
          value={filters.search}
          onChange={(v) => setFilter({ search: v })}
          placeholder="Search name, email, phone, company, city..."
          className="flex-1 min-w-[220px]"
        />
        <FilterSelect
          value={filters.source}
          onChange={(v) => setFilter({ source: v })}
          allLabel="All sources"
          options={Object.entries(SOURCE_LABEL)}
        />
        <FilterSelect
          value={filters.status}
          onChange={(v) => setFilter({ status: v })}
          allLabel="All statuses"
          options={STATUSES.map((s) => [s, capitalize(s)])}
        />
        <DateRangeFilter
          label="Submitted"
          from={filters.from}
          to={filters.to}
          onChange={({ from, to }) => setFilter({ from, to })}
        />
        <ResetFiltersButton active={filtersActive} onReset={() => setFilter(EMPTY_FILTERS)} />
        <ExportMenu
          fileName="website-leads"
          columns={EXPORT_COLUMNS}
          getRows={() => fetchAllPages(superAdminAPI.getWebsiteLeads, toApiParams(filters))}
        />
      </Card>

      <Card padding="p-0">
        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : error ? (
          <p className="text-sm text-danger-600 text-center py-8">
            {error.response?.data?.message || 'Failed to load leads'}
          </p>
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<HiOutlineInboxIn className="w-10 h-10" />}
            title="No leads yet"
            description="Submissions from Contact, Book a Demo, and Connect will show up here."
          />
        ) : (
          <Table>
            <TableHead>
              <TableRow hoverable={false}>
                <TableCell header>Name</TableCell>
                <TableCell header>Contact</TableCell>
                <TableCell header>Source</TableCell>
                <TableCell header>Status</TableCell>
                <TableCell header>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead._id} onClick={() => setViewing(lead)}>
                  <TableCell>
                    <p className="font-semibold text-gray-900">{lead.name}</p>
                    {lead.company && <p className="text-xs text-gray-500">{lead.company}</p>}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-gray-700">{lead.phone}</p>
                    {lead.email && <p className="text-xs text-gray-500">{lead.email}</p>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={SOURCE_BADGE[lead.source]}>{SOURCE_LABEL[lead.source] || lead.source}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_BADGE[lead.status]}>{lead.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-gray-500">{formatDate(lead.createdAt)}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {pagination.total > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            pageSize={filters.limit}
            onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
            onPageSizeChange={(limit) => setFilter({ limit })}
          />
        )}
      </Card>

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setViewing(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{viewing.name}</h2>
                <Badge variant={SOURCE_BADGE[viewing.source]} className="mt-1">
                  {SOURCE_LABEL[viewing.source] || viewing.source}
                </Badge>
              </div>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600">
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <HiOutlinePhone className="w-4 h-4 text-gray-400" /> {viewing.phone}
              </div>
              {viewing.email && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <HiOutlineMail className="w-4 h-4 text-gray-400" /> {viewing.email}
                </div>
              )}
              {viewing.company && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400" /> {viewing.company}
                </div>
              )}
              {viewing.city && <p className="text-sm text-gray-700"><strong>City:</strong> {viewing.city}</p>}
              {viewing.interest && <p className="text-sm text-gray-700"><strong>Interested in:</strong> {viewing.interest}</p>}
              {viewing.teamSize && <p className="text-sm text-gray-700"><strong>Team size:</strong> {viewing.teamSize}</p>}
              {viewing.industry && <p className="text-sm text-gray-700"><strong>Industry:</strong> {viewing.industry}</p>}
              {(viewing.preferredDate || viewing.preferredTime) && (
                <p className="text-sm text-gray-700">
                  <strong>Preferred slot:</strong> {viewing.preferredDate} {viewing.preferredTime}
                </p>
              )}
              {viewing.message && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Message</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewing.message}</p>
                </div>
              )}
              <p className="text-xs text-gray-400">Submitted {formatDate(viewing.createdAt)}</p>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Status</label>
                <select
                  value={viewing.status}
                  onChange={(e) => updateMutation.mutate({ id: viewing._id, body: { status: e.target.value } })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{capitalize(s)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Internal notes</label>
                <textarea
                  defaultValue={viewing.notes || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (viewing.notes || '')) {
                      updateMutation.mutate({ id: viewing._id, body: { notes: e.target.value } });
                    }
                  }}
                  rows={3}
                  placeholder="Add a note..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteLeadsList;
